import { useMemo } from "react";
import {
  Box,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { format } from "date-fns";

interface EventInstance {
  id: string;
  startDate: any;
  endDate?: any;
  description?: string;
  [key: string]: any;
}

interface InstanceSelectorProps {
  instances: EventInstance[];
  selectedInstanceId: string | null;
  onSelectInstance: (id: string) => void;
  showPast: boolean;
  onToggleShowPast: (show: boolean) => void;
  passCountsByInstance?: Record<string, number>;
}

const toDate = (val: any): Date => {
  if (val instanceof Date) return val;
  if (val?.seconds) return new Date(val.seconds * 1000);
  return new Date(val);
};

export const InstanceSelector = ({
  instances,
  selectedInstanceId,
  onSelectInstance,
  showPast,
  onToggleShowPast,
  passCountsByInstance = {},
}: InstanceSelectorProps) => {
  const now = useMemo(() => new Date(), []);

  const { upcoming, past } = useMemo(() => {
    const up: EventInstance[] = [];
    const pa: EventInstance[] = [];
    instances.forEach((i) => {
      if (toDate(i.startDate) > now) {
        up.push(i);
      } else {
        pa.push(i);
      }
    });
    up.sort(
      (a, b) => toDate(a.startDate).getTime() - toDate(b.startDate).getTime()
    );
    pa.sort(
      (a, b) => toDate(b.startDate).getTime() - toDate(a.startDate).getTime()
    );
    return { upcoming: up, past: pa };
  }, [instances, now]);

  const displayed = showPast ? past : upcoming;

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 1.5,
        }}
      >
        <ToggleButtonGroup
          value={showPast ? "past" : "upcoming"}
          exclusive
          onChange={(_, val) => {
            if (val !== null) onToggleShowPast(val === "past");
          }}
          size="small"
        >
          <ToggleButton value="upcoming">
            Upcoming ({upcoming.length})
          </ToggleButton>
          <ToggleButton value="past">Past ({past.length})</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {displayed.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          {showPast
            ? "No past instances"
            : "No upcoming instances — switch to Past to view history"}
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            maxHeight: 120,
            overflowY: "auto",
          }}
        >
          {displayed.map((instance) => {
            const date = toDate(instance.startDate);
            const count = passCountsByInstance[instance.id] || 0;
            const isSelected = instance.id === selectedInstanceId;
            return (
              <Chip
                key={instance.id}
                label={`${format(date, "MMM d, yyyy h:mm a")}${count > 0 ? ` (${count})` : ""}`}
                color={isSelected ? "primary" : "default"}
                variant={isSelected ? "filled" : "outlined"}
                onClick={() => onSelectInstance(instance.id)}
                sx={{ fontWeight: isSelected ? 600 : 400 }}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};
